import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CancelIcon from '@mui/icons-material/Cancel';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Button, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  Document_GetFilesDocument,
  Document_UploadFileMutation,
  EstateWorkItem_GetEstateWorkItemsByPortalIdDocument,
  EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
  SignatureType,
  UploadPath,
  useDocument_UploadFileMutation,
  useDocumentTag_GetAllDocumentTagsQuery,
} from 'queries';
import * as yup from 'yup';

import FormInputErrorMessage from 'common/form-input-error-message';
import FormSelectField from 'common/form-select-field';
import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { UPLOAD_LIMIT_BYTES, UPLOAD_LIMIT_STRING } from 'utils/constants';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useHasSuperAdmin from 'utils/useHasSuperAdmin';
import useIsImpersonating from 'utils/useIsImpersonating';

const useStyles = makeStyles(() => ({
  content: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
  },
  title: {
    fontWeight: 700,
    fontSize: '24px',
    marginTop: '18px',
    marginBottom: '32px',
    alignSelf: 'center',
  },
  inputBox: {
    border: '1px solid #ECEEF1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '106px',
    borderRadius: '8px',
    flexDirection: 'column',
    color: '#596674',
    padding: '27px 0',
  },
  uploadTitle: {
    fontSize: '14px',
    color: '#596674',
    '& span': {
      color: '#5ED1F0',
      cursor: 'pointer',
    },
  },
  selectContainer: {
    marginTop: '32px',
  },
  selectInput: {
    borderRadius: '8px',
    '& fieldset': {
      border: '1px solid #ECEEF1',
    },
  },
  uploadButton: {
    borderRadius: '100px',
    maxHeight: '40px',
    marginTop: '40px',
    marginBottom: '20px',
    backgroundColor: '#5ED1F0',
    color: '#1C2541',
  },
  fileTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  error: {
    border: '1px solid red',
  },
  spinner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '& span': {
      fontSize: '14px',
    },
  },
}));

type Props = {
  documentTagId?: string | null;
  title?: string;
  parentId: string;
  portalId: string;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  onExitClick?: () => void;
  onUploadComplete?: (uploadFileData: Document_UploadFileMutation) => void;
  requestedSignatureDocumentId?: string;
  estateWorkItemId?: string;
  estateInputDataInstanceId?: string;
};

type FormUploadValuesType = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  files?: any[];
  uploadPath?: UploadPath;
  documentTagId?: string;
  signatureType: SignatureType;
};

const schema = yup.object().shape({
  documentTagId: yup.string().required('Required'),
});

export const uploadPathSelectOptions = [
  { label: 'Uploads', value: UploadPath.Uploads },
  { label: 'Search Correspondence', value: UploadPath.SearchResults },
  { label: 'Generated Documents', value: UploadPath.GeneratedDocuments },
  {
    label: 'Documents for Signature',
    value: UploadPath.DocumentsForSignature,
  },
];

export const signatureTypeSelectOptions = [
  { label: 'No signature', value: SignatureType.NoSignature },
  { label: 'E-signature', value: SignatureType.ESignature },
  { label: 'Physical signature no-mail', value: SignatureType.PhysicalSignatureNoMail },
  {
    label: 'Physical signature mail-required',
    value: SignatureType.PhysicalSignatureMailRequired,
  },
  {
    label: 'Physical signature and notary no-mail',
    value: SignatureType.PhysicalSignatureAndNotaryNoMail,
  },
  {
    label: 'Physical signature and notary mail required',
    value: SignatureType.PhysicalSignatureAndNotaryMailRequired,
  },
  {
    label: 'Signature (Wet or Electronic)',
    value: SignatureType.SignatureWetOrElectronic,
  },
];

const DocumentUploadModal = ({
  documentTagId,
  title,
  showModal,
  setShowModal,
  portalId,
  parentId,
  onExitClick,
  onUploadComplete = () => {},
  requestedSignatureDocumentId,
  estateWorkItemId,
  estateInputDataInstanceId,
}: Props) => {
  const classes = useStyles();
  const isSuperAdmin = useHasSuperAdmin();
  const isImpersonating = useIsImpersonating();

  const { data: documentTagsData, loading: documentTagsDataLoading } =
    useDocumentTag_GetAllDocumentTagsQuery({
      onError: (error) => {
        showAlert({
          message: 'Error getting all document tags: ' + error.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const documentTags = documentTagsData?.data || [];
  const defaultDocumentTag = documentTagsData?.data.find((tag) => tag.name === 'Other');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    setError,
    reset,
  } = useForm<FormUploadValuesType>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    skip: !documentTagsData || documentTagsDataLoading,
    defaultValues: {
      uploadPath: undefined,
      documentTagId: documentTagId || defaultDocumentTag?.id,
      signatureType: SignatureType.NoSignature,
    },
  });

  const files = watch('files');
  const fileName = files?.length ? files[0].name : '';

  const [uploadFile] = useDocument_UploadFileMutation({
    refetchQueries: [
      {
        query: Document_GetFilesDocument,
        variables: { input: { portalId, parentId, estateWorkItemId } },
      },
      {
        query: EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
        variables: { portalId },
      },
      {
        query: EstateWorkItem_GetEstateWorkItemsByPortalIdDocument,
        variables: { portalId },
      },
    ],
    onCompleted: onUploadComplete,
    onError: (error) => {
      showAlert({
        message: 'Error uploading files: ' + error.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (!documentTagsData || documentTagsDataLoading) {
    return <Spinner />;
  }

  const onSubmit = async (values: FormUploadValuesType) => {
    const { files, uploadPath, documentTagId, signatureType } = values;
    if (!files || files.length === 0) {
      setError('files', { message: 'Required' });
      return;
    }

    const file = files[0];

    // calculate upload size
    const uploadSizeInBytes = file.size || 0;
    if (uploadSizeInBytes <= 0) {
      showAlert({
        message:
          'Error: File cannot be uploaded - Please ensure that the file is not empty and retry.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    if (uploadSizeInBytes > UPLOAD_LIMIT_BYTES) {
      showAlert({
        message: `Error: File Size Too Large - Please reduce the size of this file to less than ${UPLOAD_LIMIT_STRING} and retry.`,
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    if (
      uploadPath === UploadPath.DocumentsForSignature &&
      signatureType === SignatureType.NoSignature
    ) {
      showAlert({
        message: 'Error uploading file: Signature Type is required for Documents for Signature.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    if (!documentTagId) {
      showAlert({
        message: 'Error uploading file: Document Tag is required.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    const needsSignature =
      signatureType !== SignatureType.NoSignature || !!requestedSignatureDocumentId;

    await uploadFile({
      variables: {
        input: {
          file,
          uploadPath,
          documentTagId,
          portalId,
          parentId,
          signatureType,
          needsSignature,
          requestedSignatureDocumentId,
          estateWorkItemId,
          estateInputDataInstanceId,
        },
      },
    });
    reset();
  };

  const filesErrorMessage = errors.files?.message;

  const renderFileTitle = () => {
    if (isSubmitting) {
      return (
        <div className={classes.spinner}>
          <Spinner />
          <span>Uploading your document</span>
        </div>
      );
    }

    return (
      <div className={classes.inputBox}>
        <div className={classes.fileTitle}>
          <Typography>{fileName}</Typography>
          <IconButton
            aria-label="delete"
            color="primary"
            onClick={() => setValue('files', undefined)}
          >
            <CancelIcon />
          </IconButton>
        </div>
      </div>
    );
  };

  const renderUploadContainer = () => {
    return (
      <label
        htmlFor="file_input"
        className={clsx(classes.inputBox, {
          [classes.error]: filesErrorMessage,
        })}
      >
        <input id="file_input" {...register('files', { required: true })} type="file" hidden />
        <FileUploadIcon />

        <Typography className={classes.uploadTitle}>
          <span>Click to upload a file</span>
        </Typography>
      </label>
    );
  };

  return (
    <Modal
      setShowModal={setShowModal}
      onExitClick={() => {
        setShowModal(false);
        onExitClick && onExitClick();
      }}
      showModal={showModal}
      showActions={false}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.content}>
            <Typography className={classes.title}>{title || 'Upload'}</Typography>
            {fileName ? renderFileTitle() : renderUploadContainer()}
            {filesErrorMessage && <FormInputErrorMessage error={filesErrorMessage} />}
            {(isImpersonating || isSuperAdmin) && (
              <div className={classes.selectContainer}>
                <Controller
                  name="uploadPath"
                  control={control}
                  render={({ field: { ref: _, ...field } }) => (
                    <FormSelectField
                      {...field}
                      errorMessage={errors.uploadPath?.message}
                      options={uploadPathSelectOptions}
                      label="Select upload path"
                      placeholder="Select upload path"
                      className={classes.selectInput}
                    />
                  )}
                />
              </div>
            )}
            <div className={classes.selectContainer}>
              <Controller
                name="documentTagId"
                control={control}
                render={({ field: { ref: _, ...field } }) => (
                  <FormSelectField
                    {...field}
                    disabled={!!documentTagId}
                    errorMessage={errors.documentTagId?.message}
                    options={documentTags.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    label="Add tag"
                    placeholder="Select document type"
                    className={classes.selectInput}
                  />
                )}
              />
            </div>
            {(isImpersonating || isSuperAdmin) && (
              <div className={classes.selectContainer}>
                <Controller
                  name="signatureType"
                  control={control}
                  render={({ field: { ref: _, ...field } }) => (
                    <FormSelectField
                      {...field}
                      disabled={!!documentTagId || !!requestedSignatureDocumentId}
                      errorMessage={errors.signatureType?.message}
                      options={signatureTypeSelectOptions}
                      label="Select signature type"
                      placeholder="Select signature type"
                      className={classes.selectInput}
                    />
                  )}
                />
              </div>
            )}
            <Button
              type="submit"
              variant="outlined"
              className={classes.uploadButton}
              disabled={isSubmitting}
            >
              Upload
            </Button>
          </div>
        </form>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
};

export default DocumentUploadModal;
